import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== CHAPTER API FUNCTIONS ====================

/**
 * Get list of all chapters (Authenticated users)
 * @returns {Promise} Array of chapters
 */
export const getAllChapters = async () => {
    try {
        const response = await api.get('/chapters/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get chapters for a single level (server-side filtered, spans every topic in the level)
 * @param {number} levelId - Level ID to filter by
 * @returns {Promise} Array of chapters belonging to the level
 */
export const getChaptersByLevel = async (levelId) => {
    try {
        const response = await api.get(`/chapters/?level=${levelId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get chapters matching a title (server-side filtered). Mirrors the same "first match" semantics
 * as the previous client-side `chapters.find(c => c.title === chapterTitle)` -- title is unique
 * per topic_content, not globally, so this can still return more than one match.
 * @param {string} title - Chapter title to filter by
 * @returns {Promise} Array of chapters matching the title
 */
export const getChaptersByTitle = async (title) => {
    try {
        const response = await api.get(`/chapters/?title=${encodeURIComponent(title)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new chapter (Admin only)
 * @param {Object} chapterData - Chapter data object
 * @param {string} chapterData.title - Chapter title
 * @param {string} chapterData.description - Chapter description
 * @param {number} chapterData.topic - Topic ID
 * @param {number} chapterData.order - Chapter order
 * @returns {Promise} Created chapter data
 */
export const createChapter = async (chapterData) => {
    try {
        const response = await api.post('/chapters/', chapterData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing chapter (Admin only)
 * @param {number} chapterId - Chapter ID
 * @param {Object} chapterData - Updated chapter data
 * @returns {Promise} Updated chapter data
 */
export const updateChapter = async (chapterId, chapterData) => {
    try {
        const response = await api.patch(`/chapters/${chapterId}/`, chapterData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a chapter (Admin only)
 * @param {number} chapterId - Chapter ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteChapter = async (chapterId) => {
    try {
        const response = await api.delete(`/chapters/${chapterId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export default api;
